import { EventBus } from "@/application/common/events/EventBus";
import { ErrorHandlerService } from "@/application/common/services/ErrorHandlerService";
import { MetricsService } from "@/application/common/services/MetricsService";
import { RetryService } from "@/application/common/services/RetryService";
import type { IProjectsGateway } from "@/application/projects/ports/IProjectsGateway";
import {
  CreateProjectUseCase,
  DeleteProjectUseCase,
  GetProjectDetailsUseCase,
  GetProjectsUseCase,
  GetAllProjectsAdminUseCase,
  UpdateProjectUseCase,
} from "@/application/projects/use-cases";
import type { RecordPresenterPort } from "@/application/records/ports/RecordPresenterPort";
import type { RecordRepositoryPort } from "@/application/records/ports/RecordRepositoryPort";
import type { RecordsGatewayPort } from "@/application/records/ports/RecordsGatewayPort";
import type { ReportGenerationPort } from "@/application/records/ports/ReportGenerationPort";
import type { SetupRepositoryPort } from "@/application/records/ports/SetupRepositoryPort";
import type { ToastServicePort } from "@/application/records/ports/ToastServicePort";
import { RecordsApplicationService } from "@/application/records/services/RecordsApplicationService";
import {
  ArchiveRecordsUseCase,
  AssignRecordToProjectUseCase,
  ChangeRecordStatusUseCase,
  DeleteRecordsUseCase,
  FetchAdminRecordsListUseCase,
  FetchAdminRecordsPaginatedUseCase,
  FetchRecordDetailsUseCase,
  FetchRecordMarkdownUseCase,
  FetchRecordMetaUseCase,
  FetchContextTokensUseCase,
  FetchRecordsListUseCase,
  FetchRecordsMetaBatchUseCase,
  FetchRecordsPaginatedUseCase,
  GenerateReportUseCase,
  LoadRecordUseCase,
  RemoveFromProjectUseCase,
  RenameRecordUseCase,
  SetupRecordUseCase,
  ValidateSetupUseCase,
} from "@/application/records/use-cases";
import { GenerationDomainService } from "@/domain/records/services/GenerationDomainService";
import { RecordDomainService } from "@/domain/records/services/RecordDomainService";
import { SetupDomainService } from "@/domain/records/services/SetupDomainService";
import { ValidationService } from "@/domain/records/services/ValidationService";
import { ProjectsApiAdapter } from "@/infrastructure/projects/adapters/ProjectsApiAdapter";
import { InsightApiAdapter } from "@/infrastructure/records/adapters/InsightApiAdapter";
import { RecordApiAdapter } from "@/infrastructure/records/adapters/RecordApiAdapter";
import { ReportGenerationAdapter } from "@/infrastructure/records/adapters/ReportGenerationAdapter";
import { RecordRepository } from "@/infrastructure/records/repositories/RecordRepository";
import { ReportRepository } from "@/infrastructure/records/repositories/ReportRepository";
import { SetupRepository } from "@/infrastructure/records/repositories/SetupRepository";
import { RecordsToastNotificationService } from "@/infrastructure/records/services/ToastNotificationService";
import { RecordPresenter } from "@/interface-adapters/records/presenters/RecordPresenter";
import { RecordsGateway } from "@/interface-adapters/records/services/RecordsGateway";
import type { ProjectReportsGatewayPort } from "@/application/project-reports/ports/ProjectReportsGatewayPort";
import { ProjectReportsApiAdapter } from "@/infrastructure/project-reports/ProjectReportsApiAdapter";
import {
  CreateProjectReportUseCase,
  DeleteProjectReportUseCase,
  GetProjectReportUseCase,
  GetProjectReportsUseCase,
  UpdateProjectReportUseCase,
} from "@/application/project-reports/use-cases";

export type AuthHeadersProvider = () => Promise<Record<string, string>>;

export interface FrontendContainerConfig {
  insightApiBaseUrl: string;
  recordsApiBaseUrl?: string;
  authHeadersProvider?: AuthHeadersProvider;
}

/**
 * Frontend dependency container wires Clean Architecture layers together.
 */
export class FrontendContainer {
  private readonly singletons = new Map<string, unknown>();

  constructor(private readonly config: FrontendContainerConfig) {}

  resolveLoadRecordUseCase(): LoadRecordUseCase {
    return this.getSingleton(
      "LoadRecordUseCase",
      () =>
        new LoadRecordUseCase(
          this.resolveRecordRepository(),
          this.resolveRecordPresenter()
        )
    );
  }

  resolveGenerateReportUseCase(): GenerateReportUseCase {
    return this.getSingleton(
      "GenerateReportUseCase",
      () =>
        new GenerateReportUseCase(
          this.resolveReportGenerationAdapter(),
          this.resolveGenerationDomainService()
        )
    );
  }

  resolveSetupRecordUseCase(): SetupRecordUseCase {
    return this.getSingleton(
      "SetupRecordUseCase",
      () =>
        new SetupRecordUseCase(
          this.resolveSetupRepository(),
          this.resolveSetupDomainService()
        )
    );
  }

  resolveValidateSetupUseCase(): ValidateSetupUseCase {
    return this.getSingleton(
      "ValidateSetupUseCase",
      () => new ValidateSetupUseCase()
    );
  }

  resolveRecordPresenter(): RecordPresenterPort {
    return this.getSingleton(
      "RecordPresenter",
      () => new RecordPresenter(this.resolveRecordDomainService())
    );
  }

  resolveRecordRepository(): RecordRepositoryPort {
    return this.getSingleton(
      "RecordRepository",
      () =>
        new RecordRepository(
          this.config.recordsApiBaseUrl ?? this.config.insightApiBaseUrl,
          this.config.insightApiBaseUrl,
          this.config.authHeadersProvider
        )
    );
  }

  resolveSetupRepository(): SetupRepositoryPort {
    return this.getSingleton(
      "SetupRepository",
      () =>
        new SetupRepository(
          this.config.recordsApiBaseUrl ?? this.config.insightApiBaseUrl,
          this.config.insightApiBaseUrl,
          this.config.authHeadersProvider
        )
    );
  }

  resolveReportGenerationAdapter(): ReportGenerationPort {
    return this.getSingleton(
      "ReportGenerationAdapter",
      () =>
        new ReportGenerationAdapter(
          this.config.insightApiBaseUrl,
          this.config.authHeadersProvider
        )
    );
  }

  resolveReportRepository(): ReportRepository {
    return this.getSingleton(
      "ReportRepository",
      () =>
        new ReportRepository(
          this.config.recordsApiBaseUrl ?? this.config.insightApiBaseUrl,
          this.config.insightApiBaseUrl,
          this.config.authHeadersProvider
        )
    );
  }

  resolveRecordApiAdapter(): RecordApiAdapter {
    return this.getSingleton(
      "RecordApiAdapter",
      () =>
        new RecordApiAdapter(
          this.config.recordsApiBaseUrl ?? this.config.insightApiBaseUrl,
          this.config.authHeadersProvider
        )
    );
  }

  resolveInsightApiAdapter(): InsightApiAdapter {
    return this.getSingleton(
      "InsightApiAdapter",
      () =>
        new InsightApiAdapter(
          this.config.insightApiBaseUrl,
          this.config.authHeadersProvider
        )
    );
  }

  resolveRecordsGateway(): RecordsGatewayPort {
    return this.getSingleton(
      "RecordsGateway",
      () =>
        new RecordsGateway(
          this.resolveRecordApiAdapter(),
          this.resolveInsightApiAdapter(),
          this.resolveReportRepository()
        )
    );
  }

  resolveRecordsApplicationService(): RecordsApplicationService {
    return this.getSingleton(
      "RecordsApplicationService",
      () => new RecordsApplicationService(this.resolveRecordsGateway())
    );
  }

  resolveFetchRecordsListUseCase(): FetchRecordsListUseCase {
    return this.getSingleton(
      "FetchRecordsListUseCase",
      () => new FetchRecordsListUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchAdminRecordsListUseCase(): FetchAdminRecordsListUseCase {
    return this.getSingleton(
      "FetchAdminRecordsListUseCase",
      () => new FetchAdminRecordsListUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchRecordsPaginatedUseCase(): FetchRecordsPaginatedUseCase {
    return this.getSingleton(
      "FetchRecordsPaginatedUseCase",
      () => new FetchRecordsPaginatedUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchAdminRecordsPaginatedUseCase(): FetchAdminRecordsPaginatedUseCase {
    return this.getSingleton(
      "FetchAdminRecordsPaginatedUseCase",
      () => new FetchAdminRecordsPaginatedUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchRecordDetailsUseCase(): FetchRecordDetailsUseCase {
    return this.getSingleton(
      "FetchRecordDetailsUseCase",
      () => new FetchRecordDetailsUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchRecordMarkdownUseCase(): FetchRecordMarkdownUseCase {
    return this.getSingleton(
      "FetchRecordMarkdownUseCase",
      () => new FetchRecordMarkdownUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchRecordMetaUseCase(): FetchRecordMetaUseCase {
    return this.getSingleton(
      "FetchRecordMetaUseCase",
      () => new FetchRecordMetaUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchContextTokensUseCase(): FetchContextTokensUseCase {
    return this.getSingleton(
      "FetchContextTokensUseCase",
      () => new FetchContextTokensUseCase(this.resolveRecordsGateway())
    );
  }

  resolveFetchRecordsMetaBatchUseCase(): FetchRecordsMetaBatchUseCase {
    return this.getSingleton(
      "FetchRecordsMetaBatchUseCase",
      () => new FetchRecordsMetaBatchUseCase(this.resolveRecordsGateway())
    );
  }

  resolveDeleteRecordsUseCase(): DeleteRecordsUseCase {
    return this.getSingleton(
      "DeleteRecordsUseCase",
      () => new DeleteRecordsUseCase(this.resolveRecordsGateway())
    );
  }

  resolveArchiveRecordsUseCase(): ArchiveRecordsUseCase {
    return this.getSingleton(
      "ArchiveRecordsUseCase",
      () => new ArchiveRecordsUseCase(this.resolveRecordsGateway())
    );
  }

  resolveRenameRecordUseCase(): RenameRecordUseCase {
    return this.getSingleton(
      "RenameRecordUseCase",
      () => new RenameRecordUseCase(this.resolveRecordsGateway())
    );
  }

  resolveRetryService(): RetryService {
    return this.getSingleton(
      "RetryService",
      () =>
        new RetryService({
          maxRetries: 3,
          baseDelay: 1000,
          maxDelay: 10000,
          backoffMultiplier: 2,
        })
    );
  }

  resolveMetricsService(): MetricsService {
    return this.getSingleton("MetricsService", () => new MetricsService());
  }

  resolveErrorHandlerService(): ErrorHandlerService {
    return this.getSingleton(
      "ErrorHandlerService",
      () => new ErrorHandlerService(this.resolveMetricsService())
    );
  }

  resolveEventBus(): EventBus {
    return this.getSingleton("EventBus", () => new EventBus());
  }

  resolveRemoveFromProjectUseCase(): RemoveFromProjectUseCase {
    return this.getSingleton(
      "RemoveFromProjectUseCase",
      () =>
        new RemoveFromProjectUseCase(
          this.resolveRecordsGateway(),
          this.resolveRetryService(),
          this.resolveMetricsService(),
          this.resolveErrorHandlerService(),
          this.resolveEventBus()
        )
    );
  }

  resolveAssignRecordToProjectUseCase(): AssignRecordToProjectUseCase {
    return this.getSingleton(
      "AssignRecordToProjectUseCase",
      () =>
        new AssignRecordToProjectUseCase(
          this.resolveRecordsGateway(),
          this.resolveRetryService(),
          this.resolveMetricsService(),
          this.resolveErrorHandlerService(),
          this.resolveEventBus()
        )
    );
  }

  resolveChangeRecordStatusUseCase(): ChangeRecordStatusUseCase {
    return this.getSingleton(
      "ChangeRecordStatusUseCase",
      () =>
        new ChangeRecordStatusUseCase(
          this.resolveRecordsGateway(),
          this.resolveRetryService(),
          this.resolveMetricsService(),
          this.resolveErrorHandlerService(),
          this.resolveEventBus()
        )
    );
  }

  resolveRecordsToastService(): ToastServicePort {
    return this.getSingleton(
      "RecordsToastService",
      () => new RecordsToastNotificationService()
    );
  }

  resolveRecordDomainService(): RecordDomainService {
    return this.getSingleton(
      "RecordDomainService",
      () => new RecordDomainService()
    );
  }

  resolveGenerationDomainService(): GenerationDomainService {
    return this.getSingleton(
      "GenerationDomainService",
      () => new GenerationDomainService()
    );
  }

  resolveSetupDomainService(): SetupDomainService {
    return this.getSingleton(
      "SetupDomainService",
      () => new SetupDomainService()
    );
  }

  resolveValidationService(): typeof ValidationService {
    return ValidationService;
  }

  // Projects resolvers
  resolveProjectsGateway(): IProjectsGateway {
    return this.getSingleton("ProjectsGateway", () => new ProjectsApiAdapter());
  }

  resolveGetProjectsUseCase(): GetProjectsUseCase {
    return this.getSingleton(
      "GetProjectsUseCase",
      () => new GetProjectsUseCase(this.resolveProjectsGateway())
    );
  }

  resolveCreateProjectUseCase(): CreateProjectUseCase {
    return this.getSingleton(
      "CreateProjectUseCase",
      () => new CreateProjectUseCase(this.resolveProjectsGateway())
    );
  }

  resolveGetProjectDetailsUseCase(): GetProjectDetailsUseCase {
    return this.getSingleton(
      "GetProjectDetailsUseCase",
      () => new GetProjectDetailsUseCase(this.resolveProjectsGateway())
    );
  }

  resolveUpdateProjectUseCase(): UpdateProjectUseCase {
    return this.getSingleton(
      "UpdateProjectUseCase",
      () => new UpdateProjectUseCase(this.resolveProjectsGateway())
    );
  }

  resolveDeleteProjectUseCase(): DeleteProjectUseCase {
    return this.getSingleton(
      "DeleteProjectUseCase",
      () => new DeleteProjectUseCase(this.resolveProjectsGateway())
    );
  }

  resolveGetAllProjectsAdminUseCase(): GetAllProjectsAdminUseCase {
    return this.getSingleton(
      "GetAllProjectsAdminUseCase",
      () => new GetAllProjectsAdminUseCase(this.resolveProjectsGateway())
    );
  }

  // Project reports resolvers
  resolveProjectReportsGateway(): ProjectReportsGatewayPort {
    return this.getSingleton(
      "ProjectReportsGateway",
      () => new ProjectReportsApiAdapter()
    );
  }

  resolveGetProjectReportsUseCase(): GetProjectReportsUseCase {
    return this.getSingleton(
      "GetProjectReportsUseCase",
      () => new GetProjectReportsUseCase(this.resolveProjectReportsGateway())
    );
  }

  resolveGetProjectReportUseCase(): GetProjectReportUseCase {
    return this.getSingleton(
      "GetProjectReportUseCase",
      () => new GetProjectReportUseCase(this.resolveProjectReportsGateway())
    );
  }

  resolveCreateProjectReportUseCase(): CreateProjectReportUseCase {
    return this.getSingleton(
      "CreateProjectReportUseCase",
      () => new CreateProjectReportUseCase(this.resolveProjectReportsGateway())
    );
  }

  resolveUpdateProjectReportUseCase(): UpdateProjectReportUseCase {
    return this.getSingleton(
      "UpdateProjectReportUseCase",
      () => new UpdateProjectReportUseCase(this.resolveProjectReportsGateway())
    );
  }

  resolveDeleteProjectReportUseCase(): DeleteProjectReportUseCase {
    return this.getSingleton(
      "DeleteProjectReportUseCase",
      () => new DeleteProjectReportUseCase(this.resolveProjectReportsGateway())
    );
  }

  private getSingleton<T>(key: string, factory: () => T): T {
    if (!this.singletons.has(key)) {
      this.singletons.set(key, factory());
    }
    return this.singletons.get(key) as T;
  }
}

export function createDefaultFrontendContainer(
  overrides: Partial<FrontendContainerConfig> = {}
): FrontendContainer {
  const baseUrl = process.env.NEXT_PUBLIC_CUSTDEV_API_URL;
  if (!baseUrl) {
    throw new Error("NEXT_PUBLIC_CUSTDEV_API_URL is not set");
  }

  return new FrontendContainer({
    insightApiBaseUrl: baseUrl.replace(/\/$/, ""),
    ...overrides,
  });
}
